<template>
  <div
    v-loading="loading || currentTab == null"
    class="corp-setting-main"
  >
    <div class="corp-setting-sidebar">
      <BHeightAdjuster>
        <div class="corp-setting-menu">
          <div
            v-for="(currentTabs, index) in [ tabUsers, tabClients, tabSystemSetting, tabApiSetting ]"
            :key="index"
          >
            <div
              v-if="index !== 0 && someTabEnabled(currentTabs)"
              class="hr"
            />
            <div
              v-for="item in currentTabs"
              :key="item.tabType"
            >
              <div
                v-show="item.tabAvailable"
                class="list-item"
                :class="{ active: isCurrentTab(item.tabType) }"
                @click="() => setTabsToPath(item.tabType)"
              >
                {{ item.text }}
              </div>
            </div>
          </div>
        </div>
      </BHeightAdjuster>
    </div>
    <div class="corp-setting-content">
      <div class="modal-tab-box">
        <div class="tab-header">
          <BLayout>
            <template v-if="currentTab in subTabs">
              <div
                v-for="(tab, index) in subTabs[currentTab]"
                v-show="canDisplaySubTab(tab)"
                :key="index"
                class="tab-item"
                :class="{ active: isCurrentSubTab(tab.tabType) }"
                @click="() => setTabsToPath(currentTab, tab.tabType)"
              >
                {{ tab.tabTitle }}
              </div>
            </template>
            <template v-else-if="currentTabTitle">
              <div
                class="tab-item active"
              >
                {{ currentTabTitle }}
              </div>
            </template>
          </BLayout>
        </div>
        <div class="tab-content">
          <template v-if="isCurrentTab('userSetting')">
            <UserProfile
              v-if="isCurrentSubTab('profile')"
              class="tab-contents"
            />
          </template>
          <template v-if="isCurrentTab('member')">
            <Member
              class="tab-contents"
            />
          </template>
          <template v-if="isCurrentTab('aiSetting')">
            <TranscriptionSummarizationSetting
              v-if="isCurrentSubTab('transcriptionSummarizationSetting')"
              class="tab-contents"
            />
          </template>
          <ZoomApi
            v-if="isCurrentTab('zoom')"
            class="tab-contents"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { camelCase, kebabCase } from 'lodash';
import { computed, onMounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import TranscriptionSummarizationSetting from '@/components/organisms/user/setting/systemSetting/TranscriptionSummarizationSetting.vue';
import ZoomApi from '@/components/organisms/va-app/setting/api/ZoomApi.vue';
import Member from '@/components/organisms/va-app/setting/member/Member.vue';
import UserProfile from '@/components/organisms/va-app/setting/userSetting/UserProfile.vue';
import { useAvailableFeatureCheck } from '@/composable/available-feature-check';
import { useCurrentUser } from '@/composable/user/user/users';
import { i18nGlobal as i18n } from '@/i18n';

const {
  promiseOfFetchAvailableFeatures,
} = useAvailableFeatureCheck();
const { currentUser, fetchCurrentUser } = useCurrentUser();

const route = useRoute();
const router = useRouter();
const loading = ref<boolean>();
const currentTab = ref<string>(null);
const currentSubTab = ref<string>(null);

const subTabs = computed(() => {
  return {
    'userSetting': [
      { tabType: 'profile', tabTitle: i18n.t('corpSettingApi.userSetting.subTabs.profile'), tabAvailable: true },
    ],
    'aiSetting': [
      { tabType: 'transcriptionSummarizationSetting', tabTitle: i18n.t('corpSettingApi.aiSetting.subTabs.transcriptionSummarizationSetting'), tabAvailable: isCurrentUserHasOwnerRole.value },
    ],
  };
});

const tabUsers = computed(() => {
  return [
    { text: i18n.t('corpSettingApi.tab.userSetting.title'), tabType: 'userSetting', tabTitle: '', tabAvailable: true },
  ];
});

const tabClients = computed(() => {
  return [
    { text: i18n.t('corpSettingApi.tab.member.title'), tabType: 'member', tabTitle: i18n.t('corpSettingApi.member'), tabAvailable: true },
  ];
});

const tabApiSetting = computed(() => {
  return [
    { text: i18n.t('corpSettingApi.tab.zoom.title'), tabType: 'zoom', tabTitle: i18n.t('corpSettingApi.api'), tabAvailable: true },
  ];
});

const tabSystemSetting = computed(() => {
  return [
    { text: i18n.t('corpSettingApi.tab.aiSetting.title'), tabType: 'aiSetting', tabTitle: '', tabAvailable: isCurrentUserHasOwnerRole.value },
  ];
});

const tabs = computed(() => {
  return [
    ...tabUsers.value,
    ...tabClients.value,
    ...tabApiSetting.value,
    ...tabSystemSetting.value,
  ];
});

const currentTabTitle = computed(() => {
  return tabs.value.find(tab => tab.tabType === currentTab.value)?.tabTitle;
});

const isCurrentUserHasOwnerRole = computed(() => {
  return currentUser.value?.role === 'owner';
});

onMounted(async () => {
  await fetchCurrentUser();

  const tab = route.params.tab;
  const subTab = route.params.subTab;
  const tabType = tab ? camelCase(tab.toString()) : null;
  const subTabType = subTab ? camelCase(subTab.toString()) : null;
  setSelectTab(tabType, subTabType);
});

const isCurrentTab = (tabType) => {
  return currentTab.value === tabType;
};

const isCurrentSubTab = (tabType) => {
  if (currentTab.value in subTabs.value) {
    return currentSubTab.value === tabType;
  }
  return true;
};

const setTabsToPath = (tabType: string, subTabType?: string) => {
  router.push({
    name: 'VaSetting',
    params: { tab: kebabCase(tabType), subTab: kebabCase(subTabType) },
  });
};

const isTabAvailable = (tabType?: string | null): boolean => {
  if (!tabType) return false;
  return tabs.value.find(tab => tab.tabType === tabType)?.tabAvailable || false;
};

const isSubTabAvailable = (tabType?: string | null, subTabType?: string | null): boolean => {
  if (!tabType || !subTabType) return false;
  return subTabs.value[tabType]?.find(subTab => subTab.tabType === subTabType)?.tabAvailable || false;
};

const setSelectTab = async (tabType?: string | null, subTabType?: string | null) => {
  await promiseOfFetchAvailableFeatures;
  // NOTE: invalid/無効なタブにはアクセスさせない
  currentTab.value = isTabAvailable(tabType) ? tabType : null;
  currentSubTab.value = isSubTabAvailable(currentTab.value, subTabType) ? subTabType : null;
  if (currentTab.value && currentSubTab.value === null && currentTab.value in subTabs.value) {
    // 有効な子タブをアクティブにする
    subTabs.value[currentTab.value].some(subTab => {
      if (isSubTabAvailable(currentTab.value, subTab.tabType)) {
        currentSubTab.value = subTab.tabType;
        return true;
      }
    });
    // 有効な子タブがない時は親タブもアクセス不可
    if (currentSubTab.value == null) {
      currentTab.value = null;
    }
  }
  if (currentTab.value == null) {
    currentTab.value = 'userSetting';
  }

  router.replace({
    name: 'VaSetting',
    params: { tab: kebabCase(currentTab.value), subTab: kebabCase(currentSubTab.value) },
    query: route?.query,
  });
};

const someTabEnabled = (tabs) => {
  return tabs.some(tab => tab.tabAvailable);
};

const canDisplaySubTab = (tab) => {
  return tab.tabAvailable;
};
</script>

<style lang="scss" scoped>
$corp-setting-content-margin-top: 100px;
.corp-setting-main {
  position: relative;
  width: 100%;
}

.corp-setting-sidebar {
  @include m-fixed-width($setting-sidebar-width);
  @include m-fixed-height($page-height);
  margin-top: 50px;
  filter: drop-shadow(0px 4px 10px rgba(0, 0, 0, 0.06));
  z-index: 8;
  box-shadow: 5px 0 5px -5px $bdcolor-base;
}
.corp-setting-menu {
  margin-top: 8px;

  .list-item {
    @include m-fixed-height($setting-item-height);
    position: relative;
    display: flex;
    align-items: center;
    color: #000000;
    text-decoration: none;
    padding: $basespace-100 $basespace-400;
    margin-bottom: $basespace-50;
    transition: $transition-base;
    cursor: pointer;

    &:hover {
      background-color: $bgcolor-dark;
    }

    &.active {
      transition: none;
      background: linear-gradient(90deg, rgba(43, 192, 184, 0.5) -4%, rgba(43, 192, 184, 0) 100%);
      color: $basecolor-primary;
    }

    &.active::before {
      content: "";
      display: inline-block;
      width: 3px;
      height: 100%;
      background-color: $basecolor-success;
      position: absolute;
      left: 0;
    }
  }

  .hr {
    border-bottom: 1px solid $bdcolor-base;
    margin-bottom: 8px;
    margin-top: 8px;
  }
}
.corp-setting-content {
  position: absolute;
  top: 0;
  left: 250px;
  height: 100%;
  width: calc(100% - 250px);
  margin-top: $corp-setting-content-margin-top;

  .modal-tab-box {
    // TODO: 数値を埋め込まない
    height: calc(100% - $corp-setting-content-margin-top);

    .tab-header {
      // TODO: 数値を埋め込まない
      height: 23px;
      border-bottom: 1px solid $bdcolor-base;

      .tab-item {
        margin: 0 $basespace-600;
        position: relative;
        top: 1px;
        transition: $transition-base;
        font-size: $fontsize-200;
        line-height: 1;
        text-align: center;
        padding: 0 0 6px;
        color: $textcolor-base;
        border-bottom: 2px solid transparent;
        cursor: pointer;
        font-weight: 600;
        min-width: 56px;

        &:hover {
          color: $basecolor-primary;
        }

        &.active {
          color: $basecolor-primary;
          border-color: $basecolor-primary;
        }
      }

      .header-submenu-area {
        position: absolute;
        right: 56px;
        bottom: 20px;

        .header-submenu {
          display: flex;
          justify-content: space-between;
          justify-items: center;
          width: 100%;
          height: 100%;
        }
      }
    }

    .tab-contents {
      height: 100%;
    }
  }

  .tab-content {
    height: calc(100% - 23px);
  }
}
</style>
